import { useEffect } from "react";
import { useLocation } from "react-router-dom";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  canonical?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleAuthor?: string;
}

const SEO = ({ 
  title = "Neil AI Global Solutions – AI Automation & PUDU Robotics Dealer",
  description = "Go fully autonomous with Neil AI — offering robotics, AI voice automation, CRM systems & digital integration for businesses nationwide.",
  keywords = "AI automation, PUDU robotics, service robots, delivery robots, cleaning robots, voice automation, CRM integration, business automation, AI solutions, robotics dealer",
  ogImage = "https://storage.googleapis.com/gpt-engineer-file-uploads/8SEUVb8JvPcIZjhsVe7dXyjWUY22/social-images/social-1762627978213-neil ai gs logo new.png",
  canonical = "https://neilaiglobalsolutionsllc.net/",
  articlePublishedTime,
  articleModifiedTime,
  articleAuthor
}: SEOProps) => {
  const location = useLocation();

  useEffect(() => {
    // Update title
    document.title = title;

    // Update meta tags with security headers
    const metaTags = {
      description,
      keywords,
      'robots': 'index,follow',
      'og:title': title,
      'og:description': description,
      'og:image': ogImage,
      'og:url': canonical || window.location.href,
      'og:type': 'website',
      'twitter:card': 'summary_large_image',
      'twitter:title': title,
      'twitter:description': description,
      'twitter:image': ogImage,
    };

    // Security headers
    const securityHeaders = {
      'X-Content-Type-Options': 'nosniff',
      'X-Frame-Options': 'SAMEORIGIN',
      'Referrer-Policy': 'strict-origin-when-cross-origin'
    };

    Object.entries(metaTags).forEach(([key, value]) => {
      const attribute = key.startsWith('og:') || key.startsWith('twitter:') ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${key}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', value);
    });

    // Add security headers
    Object.entries(securityHeaders).forEach(([key, value]) => {
      let element = document.querySelector(`meta[http-equiv="${key}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('http-equiv', key);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', value);
    });

    // Article metadata
    if (articlePublishedTime) {
      let element = document.querySelector('meta[property="article:published_time"]');
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', 'article:published_time');
        document.head.appendChild(element);
      }
      element.setAttribute('content', articlePublishedTime);
    }
    
    if (articleModifiedTime) {
      let element = document.querySelector('meta[property="article:modified_time"]');
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', 'article:modified_time');
        document.head.appendChild(element);
      }
      element.setAttribute('content', articleModifiedTime);
    }
    
    if (articleAuthor) {
      let element = document.querySelector('meta[property="article:author"]');
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', 'article:author');
        document.head.appendChild(element);
      }
      element.setAttribute('content', articleAuthor);
    }

    // Enhanced structured data with Organization, LocalBusiness, and FAQ
    const structuredData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": "https://neilaiglobalsolutionsllc.net/#org",
          "name": "Neil AI Global Solutions",
          "legalName": "Neil AI Global Solutions LLC",
          "url": "https://neilaiglobalsolutionsllc.net/",
          "logo": "https://neilaiglobalsolutionsllc.net/logo.png",
          "sameAs": [
            "https://www.facebook.com/neilaiglobalsolutions",
            "https://www.instagram.com/neilaiglobalsolutions",
            "https://www.tiktok.com/@neilaiglobalsolutions",
            "https://www.linkedin.com/company/neil-ai-global-solutions"
          ]
        },
        {
          "@type": "LocalBusiness",
          "@id": "https://neilaiglobalsolutionsllc.net/#local",
          "name": "Neil AI Global Solutions",
          "url": "https://neilaiglobalsolutionsllc.net/",
          "telephone": "+1-336-962-4539",
          "email": "neilai@neilaiglobalsolutionsllc.com",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Greensboro",
            "addressLocality": "Greensboro",
            "addressRegion": "NC",
            "postalCode": "27401",
            "addressCountry": "US"
          },
          "image": "https://neilaiglobalsolutionsllc.net/logo.png",
          "description": "Neil AI Global Solutions LLC helps businesses automate with AI, robotics and voice technology. Official Pudu Robotics dealer delivering cleaning, delivery and service robots plus AI clone and automation software."
        },
        {
          "@type": "FAQPage",
          "@id": "https://neilaiglobalsolutionsllc.net/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Is Neil AI Global Solutions a real company?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes. Neil AI Global Solutions LLC is a registered US company based in North Carolina, providing AI software, robotics and automation services to real businesses, schools, churches and organizations."
              }
            },
            {
              "@type": "Question",
              "name": "Do you offer support and training?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes. Every automation or robotics deployment includes onboarding, training for your staff, and ongoing support options."
              }
            },
            {
              "@type": "Question",
              "name": "What brands do you work with?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We are an authorized dealer and integration partner for PUDU Robotics and other trusted technology vendors."
              }
            }
          ]
        }
      ]
    };

    let scriptTag = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement | null;
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.type = 'application/ld+json';
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(structuredData);

    // Update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonical || window.location.href);

    // Track page view
    if (window.gtag) {
      window.gtag('config', 'G-XXXXXXXXXX', {
        page_path: location.pathname,
      });
    }

    if (window.fbq) {
      window.fbq('track', 'PageView');
    }
  }, [title, description, keywords, ogImage, canonical, location.pathname]);

  return null;
};

export default SEO;
